import { useForm } from '@tanstack/react-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Button } from '../ui/button';
import { Field, FieldGroup, FieldLabel } from '../ui/field';
import { Input } from '../ui/input';
import { Spinner } from '../ui/spinner';
import { Textarea } from '../ui/textarea';
import { SOCIAL_HISTORY_TOPICS } from './socialHistoryUtils';
import { ValueSetCombobox, type ValueSetCode } from './ValueSetCombobox';

export type { ValueSetCode };

const formSchema = z.object({
  topic: z.custom<ValueSetCode | undefined>().refine((v): v is ValueSetCode => v !== undefined),
  value: z.string().trim().min(1),
  notes: z.string().trim(),
});

export type AddSocialHistoryFormValues = z.infer<typeof formSchema>;

export interface AddSocialHistoryFormProps {
  onCreate: (values: AddSocialHistoryFormValues) => Promise<void>;
  onClose: () => void;
  isCreating: boolean;
}

export function AddSocialHistoryForm({ onCreate, onClose, isCreating }: AddSocialHistoryFormProps): React.ReactNode {
  const { t } = useTranslation('records');

  const form = useForm({
    defaultValues: {
      topic: undefined as ValueSetCode | undefined,
      value: '',
      notes: '',
    },
    validators: { onChange: formSchema, onMount: formSchema, onSubmit: formSchema },
    onSubmit: async ({ value }) => {
      await onCreate(formSchema.parse(value));
    },
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void form.handleSubmit();
      }}
      className="space-y-3"
    >
      <FieldGroup>
        <form.Field name="topic">
          {(field) => (
            <ValueSetCombobox
              id={field.name}
              label={t('socialHistoryTopic')}
              placeholder={t('socialHistoryTopicPlaceholder')}
              items={[...SOCIAL_HISTORY_TOPICS]}
              value={field.state.value ?? null}
              onValueChange={(value) => field.handleChange(value ?? undefined)}
              onQueryChange={() => undefined}
              isLoading={false}
              emptyMessage={t('noMatchingResults')}
              required
            />
          )}
        </form.Field>

        <form.Field name="value">
          {(field) => (
            <Field>
              <FieldLabel htmlFor={field.name}>{t('socialHistoryValue')}</FieldLabel>
              <Input
                id={field.name}
                name={field.name}
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                placeholder={t('socialHistoryValuePlaceholder')}
                aria-label={t('socialHistoryValue')}
                aria-required
                required
              />
            </Field>
          )}
        </form.Field>

        <form.Field name="notes">
          {(field) => (
            <Field>
              <FieldLabel htmlFor={field.name}>{t('notes')}</FieldLabel>
              <Textarea
                id={field.name}
                name={field.name}
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                placeholder={t('notePlaceholder')}
                aria-label={t('notes')}
              />
            </Field>
          )}
        </form.Field>
      </FieldGroup>

      <div className="flex gap-2">
        <form.Subscribe selector={(state) => [state.canSubmit, state.isSubmitting] as const}>
          {([canSubmit, isSubmitting]) => (
            <Button
              type="submit"
              size="sm"
              disabled={!canSubmit || isSubmitting || isCreating}
              className="flex-1"
              aria-label={t('saveNewSocialHistory')}
            >
              {isSubmitting || isCreating ? <Spinner className="size-4" /> : t('add')}
            </Button>
          )}
        </form.Subscribe>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={onClose}
          disabled={isCreating}
          className="flex-1"
          aria-label={t('cancelAddSocialHistory')}
        >
          {t('cancel')}
        </Button>
      </div>
    </form>
  );
}
